import { TextareaComponent, type TextareaProps } from '@src/components/textarea/Textarea.tsx';
import { ComboboxSingle, type ComboboxSingleProps } from '@src/components/combobox/ComboboxSingle.tsx';
import { ComboboxBehavior, type ComboboxBehaviorProps } from '@src/components/combobox/ComboboxBehavior.tsx';
import { ComboboxScenarioPicker } from '@src/components/combobox/ComboboxScenarioPicker';
import { ComboboxGotoPicker } from '@src/components/combobox/ComboboxGotoPicker';
import { MessageCombobox, type MessageComboboxProps } from '@src/components/combobox/Combobox.tsx';
import { InputComponent, type InputProps } from '@src/components/input/Input.tsx';
import { Selectbox, type SelectProps } from '@src/components/selectbox/Selectbox.tsx';
import { DynamicSelection, type DynamicSelectionProps } from '@src/components/dynamicSelector/DynamicItemsSelector.tsx';
import {
  MultiOptionsSelector,
  type MultiOptionsSelectorProps
} from '@src/components/multi-options/MultiOptionsSelector.tsx';
import { SwitchComponent, type SwitchProps } from '@src/components/switch/Switch.tsx';
import { QuestionIdField } from '@src/components/node-info/fields/QuestionIdField';

type CommonProps =
  TextareaProps
  | InputProps
  | SelectProps
  | MessageComboboxProps
  | ComboboxBehaviorProps
  | MultiOptionsSelectorProps
  | SwitchProps
  | ComboboxSingleProps;

type CommonComponent = (props: CommonProps) => JSX.Element;

const TextareaWithAlternativesComponent = ((props: TextareaProps) =>
  <TextareaComponent
    {...props}
    withAlternatives
  />) as CommonComponent;

const ComboboxWithSingleValueComponent = ((props: ComboboxSingleProps) =>
  <ComboboxSingle
    {...props}
  />) as CommonComponent;

const ComboboxWithCustomOptionsComponent = ((props: MessageComboboxProps) =>
  <MessageCombobox
    {...props}
    withCustomOptions
  />) as CommonComponent;

const ComboboxWithSingleValueAndCustomOptionsComponent = ((props: ComboboxSingleProps) =>
  <ComboboxSingle
    {...props}
    withCustomOptions
  />) as CommonComponent;

const FactSelectorComponent = ((props: DynamicSelectionProps) =>
  <DynamicSelection
    {...props}
    fetchItemsMethod="fetchFitFacts"
    fetchFilteredItemsMethod="fetchFilteredFitFacts"
    pickItemsMethod="pickFitFacts"
    itemName="FitFact"
  />) as CommonComponent;

const ArticleSelectorComponent = ((props: DynamicSelectionProps) =>
  <DynamicSelection
    {...props}
    fetchItemsMethod="fetchArticles"
    fetchFilteredItemsMethod="fetchFilteredArticles"
    fetchReferencesMethod="fetchArticleReferences"
    pickItemsMethod="pickArticles"
    browsable
    itemName="Article"
    withDynamicMaxNumber
  />) as CommonComponent;

const MultiOptionsSelectorWithImageComponent = ((props: MultiOptionsSelectorProps) =>
  <MultiOptionsSelector
    {...props}
    withImageSelection
  />) as CommonComponent;

export const getComponentByFieldType = (fieldType: string): CommonComponent => {
  switch (fieldType) {
    case 'input':
      return InputComponent as CommonComponent;
    case 'textarea':
      return TextareaComponent as CommonComponent;
    case 'textarea-with-alternatives':
      return TextareaWithAlternativesComponent;
    case 'select':
      return Selectbox as CommonComponent;
    case 'combobox':
      return MessageCombobox as CommonComponent;
    case 'combobox-with-single-value':
      return ComboboxWithSingleValueComponent;
    case 'combobox-with-custom-options':
      return ComboboxWithCustomOptionsComponent;
    case 'combobox-with-single-value-and-custom-options':
      return ComboboxWithSingleValueAndCustomOptionsComponent;
    case  'combobox-behavior':
      return ComboboxBehavior as CommonComponent;
    case 'multi-options':
      return MultiOptionsSelector as CommonComponent;
    case 'multi-options-with-image':
      return MultiOptionsSelectorWithImageComponent;
    case 'switch':
      return SwitchComponent as CommonComponent;
    case 'scenario-picker':
      return ComboboxScenarioPicker as CommonComponent;
    case 'goto-picker':
      return ComboboxGotoPicker as CommonComponent;
    case 'facts':
      return FactSelectorComponent;
    case 'articles':
      return ArticleSelectorComponent;
    case 'question-id-with-duplicate-warning':
      return QuestionIdField as CommonComponent;
    default:
      return TextareaComponent as CommonComponent;
  }
}
